import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Loader2, FileText, Image as ImageIcon, X } from "lucide-react";

const iconFor = (type = "") =>
  type.includes("pdf") ? FileText : type.startsWith("image") ? ImageIcon : Paperclip;

// Admin drop-zone: uploads documents (PDF/JPG/PNG/WEBP) and stores lightweight
// { url, name, type, size } metadata on the block's `attachments` array.
export const AttachmentUploader = ({ attachments = [], onChange, testid }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/admin/proposals/tools/upload-attachment", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Échec : ${f.name}`);
      }
    }
    // Functional append onto the freshest attachments — never clobber a concurrent edit.
    if (added.length) onChange((prev = []) => [...prev, ...added]);
    if (added.length) toast.success(`${added.length} document(s) ajouté(s).`);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label className="label-eyebrow flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Pièces jointes</label>
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#96836B]" />}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        multiple
        disabled={busy}
        onChange={(e) => upload(Array.from(e.target.files || []))}
        data-testid={testid}
        className="text-xs file:mr-2 file:px-3 file:py-1.5 file:border file:border-[#1A1B18] file:bg-white hover:file:bg-[#1A1B18] hover:file:text-white file:cursor-pointer file:text-[10px] file:uppercase file:tracking-wider file:rounded-none"
      />
      <span className="text-[10px] text-[#737373]">PDF · JPG · PNG · WEBP · 5 Mo max — confirmations, vouchers, billets…</span>
      {attachments.length > 0 && (
        <ul className="pt-1 space-y-1.5">
          {attachments.map((a, i) => {
            const Icon = iconFor(a.type);
            return (
              <li key={i} className="flex items-center gap-2 text-sm text-[#4d4d4d]">
                <Icon className="h-4 w-4 text-[#96836B] shrink-0" />
                <span className="truncate flex-1">{a.name}</span>
                <button
                  type="button"
                  onClick={() => onChange((prev = []) => prev.filter((_, j) => j !== i))}
                  className="w-5 h-5 rounded-full bg-[#FBEDE7] text-[#7A4B3B] flex items-center justify-center shrink-0"
                  title="Retirer"
                ><X className="h-3 w-3" /></button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AttachmentUploader;
